import { useState, useEffect, useRef } from "react";
import { G } from "@/brand";
import api, { apiError } from "@/lib/api";
import { Rocket, Fingerprint, SlidersHorizontal, Plug, Music, Video, Bell, KeyRound } from "lucide-react";

const SUITE = [
  {
    icon: Plug, n: "01", name: "Prompt Savant", tag: "LIVE · YOU'RE HERE",
    accent: G.gold, live: true,
    desc: "The AI music prompt library. Runs standalone — and plugs directly into VP I/O, Variant, and VPV-Ai, so you can type and enter AI video prompts right from Prompt Savant.",
  },
  {
    icon: Fingerprint, n: "02", name: "Vocal DNA Fingerprint", tag: "COMING SOON",
    accent: G.muted, live: false,
    desc: "Captures your voice and yours alone — no one else's. You own your clone and the rights to use it, sealed with its own watermark and accessible only inside the app.",
  },
  {
    icon: KeyRound, n: "03", name: "Key Chord Pro", tag: "FREE BONUS · WITH VOCAL DNA",
    accent: G.gold, live: false,
    desc: "What chord are you singing in? A tool included free to enhance the Clone phase.",
  },
  {
    icon: Music, n: "04", name: "VP I/O · Variant Promptr I/O", tag: "COMING SOON",
    accent: G.muted, live: false,
    desc: "Training AI to generate music inside the app — circumventing every outside AI music generator source, or choose to funnel external AI music generators as well! The whole library becomes a module here if you so choose: one codex, two entry points.",
  },
  {
    icon: SlidersHorizontal, n: "05", name: "Variant — the DAiW", tag: "COMING SOON",
    accent: G.muted, live: false,
    desc: "The AI digital audio workstation that ties the suite together — from prompt, to voice, to finished production.",
  },
  {
    icon: Video, n: "06", name: "VPV-Ai · Variant Promptr Video", tag: "COMING SOON · FINAL",
    accent: G.orange, live: false,
    desc: "The video portion where it all comes together. Export your tracks into signature videos and short clips — the signature of it all. An all-inclusive suite where everything is created and never leaves the app.",
  },
];

export function ComingSoonModal({ open, onClose }) {
  const [notifyApp, setNotifyApp] = useState(null);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({});
  const [lit, setLit] = useState(() => new Set([0]));
  const scrollRef = useRef(null);
  const itemRefs = useRef([]);

  const recomputeLit = () => {
    const c = scrollRef.current;
    if (!c) return;
    const line = c.scrollTop + c.clientHeight * 0.82;
    const next = new Set([0]);
    itemRefs.current.forEach((el, i) => { if (el && el.offsetTop <= line) next.add(i); });
    if (c.scrollTop + c.clientHeight >= c.scrollHeight - 4) {
      for (let i = 0; i < itemRefs.current.length; i++) next.add(i);
    }
    setLit(next);
  };

  useEffect(() => {
    if (open) {
      api.get("/waitlist/counts").then(({ data }) => setCounts(data)).catch(() => {});
      setLit(new Set([0]));
      requestAnimationFrame(() => requestAnimationFrame(recomputeLit));
    }
  }, [open]);

  const handleScroll = () => recomputeLit();

  if (!open) return null;

  const submit = async () => {
    if (!email.trim() || !notifyApp) return;
    setError(""); setLoading(true);
    try {
      await api.post("/waitlist", { email, app: notifyApp });
      setCounts((c) => ({ ...c, [notifyApp]: (c[notifyApp] || 0) + 1 }));
      setDone(notifyApp); setNotifyApp(null); setEmail("");
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const close = () => { setNotifyApp(null); setEmail(""); setDone(""); setError(""); onClose(); };

  return (
    <div data-testid="coming-soon-modal" onClick={close} style={overlay}>
      <div ref={scrollRef} onScroll={handleScroll} onClick={(e) => e.stopPropagation()} style={{ ...box, position: "relative" }}>
        <button onClick={close} data-testid="coming-soon-close" style={closeBtn}>×</button>
        <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 10 }}>COMING SOON</div>
        <div style={{ fontSize: 26, fontWeight: 300, fontStyle: "italic", color: "#fff", marginBottom: 8 }}>
          One suite. Six tools. Nothing leaves the app.
        </div>
        <div style={{ color: G.muted, fontSize: 14.5, fontStyle: "italic", marginBottom: 24, lineHeight: 1.6 }}>
          Prompt Savant is the front door to an integrated creator suite — prompt, voice, music, mix, and video, all in one place.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {SUITE.map((s, idx) => {
            const on = lit.has(idx);
            const litColor = s.accent === G.muted ? G.goldLight : s.accent;
            return (
            <div key={s.n} ref={(el) => (itemRefs.current[idx] = el)} data-testid={`suite-${s.n}`} style={{ display: "flex", gap: 16, alignItems: "flex-start", borderLeft: `2px solid ${on ? litColor : "#332913"}`, background: on ? "#16130a" : "transparent", padding: "12px 16px", borderRadius: 4, opacity: on ? 1 : 0.5, boxShadow: on ? "0 8px 20px rgba(0,0,0,.5), inset 0 1px 0 rgba(201,168,76,.06)" : "none", transition: "opacity .45s ease, border-color .45s ease, background-color .45s ease, box-shadow .45s ease" }}>
              <s.icon size={22} color={on ? litColor : "#4a3f22"} style={{ flexShrink: 0, marginTop: 4, transition: "color .45s ease, filter .45s ease", filter: on ? `drop-shadow(0 0 11px ${litColor})` : "none" }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 18, color: "#fff" }}>{s.name}</span>
                  <span className="mono" style={{ fontSize: 8, letterSpacing: ".12em", color: s.accent, border: `1px solid ${s.accent}55`, padding: "2px 7px" }}>{s.tag}</span>
                  {!s.live && counts[s.name] > 0 && (
                    <span data-testid={`waiting-${s.n}`} className="mono" style={{ fontSize: 8, letterSpacing: ".12em", color: G.gold }}>● {counts[s.name]} WAITING</span>
                  )}
                </div>
                <div style={{ color: G.muted, fontSize: 14, fontStyle: "italic", marginTop: 5, lineHeight: 1.55 }}>{s.desc}</div>
                {!s.live && (
                  done === s.name ? (
                    <div className="mono" style={{ fontSize: 9, letterSpacing: ".12em", color: G.gold, marginTop: 8 }}>✓ YOU'RE ON THE LIST</div>
                  ) : (
                    <button data-testid={`notify-${s.n}`} onClick={() => { setNotifyApp(s.name); setError(""); }} className="mono"
                      style={{ marginTop: 9, padding: "6px 12px", fontSize: 8, letterSpacing: ".12em", cursor: "pointer", textTransform: "uppercase",
                        background: notifyApp === s.name ? G.gold : "transparent", color: notifyApp === s.name ? G.black : G.muted,
                        border: `1px solid ${notifyApp === s.name ? G.gold : G.border}`, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Bell size={11} /> Notify me at launch
                    </button>
                  )
                )}
              </div>
            </div>
            );
          })}
        </div>

        {notifyApp && (
          <div data-testid="notify-form" style={{ marginTop: 22, borderTop: `1px solid ${G.border}44`, paddingTop: 18 }}>
            <div className="mono" style={{ fontSize: 9, letterSpacing: ".2em", color: G.gold, marginBottom: 10 }}>NOTIFY ME ABOUT {notifyApp.toUpperCase()}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="notify-email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="you@email.com" style={{ flex: 1, padding: "11px 14px", background: G.panel, border: `1px solid ${G.border}`, color: G.text, fontSize: 14 }} />
              <button data-testid="notify-submit" onClick={submit} disabled={loading || !email} className="btn-gold" style={{ padding: "0 20px", fontSize: 10 }}>
                {loading ? "…" : "Join"}
              </button>
            </div>
            {error && <div className="mono" style={{ color: "#ff5555", fontSize: 11, marginTop: 8 }}>{error}</div>}
          </div>
        )}

        <div className="mono" style={{ fontSize: 9, letterSpacing: ".18em", color: G.goldDim, marginTop: 26, textAlign: "center" }}>
          VP I/O subscribers will get Prompt Savant access built in.
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const box = { background: G.dark, border: `1px solid ${G.borderHot}`, padding: "40px 36px", maxWidth: 560, width: "100%", maxHeight: "80vh", overflowY: "auto", scrollBehavior: "smooth", WebkitOverflowScrolling: "touch", boxShadow: "0 40px 90px rgba(0,0,0,0.75), 0 12px 30px rgba(0,0,0,0.6), 0 0 40px rgba(201,168,76,0.08)" };
const closeBtn = { position: "absolute", top: 14, right: 20, background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 24, lineHeight: 1 };
